/**
 * Promotional offers configuration
 * Handles temporary pricing offers like Black Friday, early client discounts, etc.
 */

export type OfferType = "black_friday" | "early_client" | "early_adopter" | "launch" | "custom"

export interface Promotion {
  id: string
  name: string
  description: string
  offerType: OfferType
  startDate: string // ISO date string
  endDate: string // ISO date string
  active: boolean
  // Discount configuration
  discountPercentage: number // e.g., 30 for 30% off
  // Which plans this applies to
  applicablePlans: ("creator" | "dreamer")[]
  applicableIntervals: ("monthly" | "yearly")[]
  // Stripe coupon code (if using promotion codes)
  stripeCouponId?: string
  // Visual decorations
  badgeText?: string // e.g., "Black Friday", "Limited Time"
  badgeColor?: "primary" | "destructive" | "default" | "secondary"
  showCountdown?: boolean
  // Early client tracking
  maxRedemptions?: number // For early client offers
  currentRedemptions?: number // Track how many have used it
}

/**
 * Active promotions configuration
 * Update these dates and details for your offers
 */
export const PROMOTIONS: Promotion[] = [
  {
    id: "black-friday-2025",
    name: "Black Friday",
    description: "Special Black Friday pricing - limited time only!",
    offerType: "black_friday",
    startDate: "2025-11-19T00:00:00Z", // Adjust to your Black Friday date
    endDate: "2025-11-28T23:59:59Z", // End of Cyber Monday week
    active: true,
    discountPercentage: 20, // 20% off
    applicablePlans: ["creator", "dreamer"],
    applicableIntervals: ["monthly", "yearly"],
    stripeCouponId: "BLACKFRIDAY2025", // Create this in Stripe
    badgeText: "Black Friday",
    badgeColor: "destructive",
    showCountdown: true,
  },
  {
    id: "early-client",
    name: "Early Bird Special!",
    description: "Special pricing for first clients - thank you for being here!",
    offerType: "early_client",
    startDate: "2025-11-29T00:00:00Z", 
    endDate: "2025-12-22T23:59:59Z", 
    active: true,
    discountPercentage: 40, // 15% off for early clients
    applicablePlans: ["creator", "dreamer"],
    applicableIntervals: ["monthly", "yearly"],
    stripeCouponId: "EARLYBIRD", // Create this in Stripe
    badgeText: "EARLY BIRD",
    badgeColor: "destructive",
    showCountdown: true,
  },
  {
    id: "new-year-2026",
    name: "Happy New Year 2026!",
    description: "Special pricing for first clients - thank you for being here!",
    offerType: "early_client",
    startDate: "2025-12-23T00:00:00Z", 
    endDate: "2026-01-05T23:59:59Z",
    active: true,
    discountPercentage: 20,
    applicablePlans: ["creator", "dreamer"],
    applicableIntervals: ["monthly", "yearly"],
    stripeCouponId: "NEWYEAR2026", // Create this in Stripe
    badgeText: "NEW YEAR 2026",
    badgeColor: "destructive",
    showCountdown: true,
  },
  {
    id: "early-adopter",
    name: "Early Adopter Special!",
    description: "Special pricing for early adopters - thank you for your support!",
    offerType: "early_adopter",
    startDate: "2026-01-06T00:00:00Z", 
    endDate: "2026-04-06T23:59:59Z", 
    active: true,
    discountPercentage: 40, 
    applicablePlans: ["creator", "dreamer"],
    applicableIntervals: ["monthly", "yearly"],
    stripeCouponId: "EARLYADOPTER", // Create this in Stripe
    badgeText: "EARLY ADOPTER",
    badgeColor: "destructive",
    showCountdown: true,
  },
  {
    id: "summer-sale-2026",
    name: "Summer Deal!",
    description: "Special pricing for the end of the summer!",
    offerType: "custom",
    startDate: "2026-07-31T00:00:00Z", 
    endDate: "2026-08-31T23:59:59Z", 
    active: true,
    discountPercentage: 50, 
    applicablePlans: ["creator", "dreamer"],
    applicableIntervals: ["yearly"],
    stripeCouponId: "SUMMERSALE",
    badgeText: "SUMMER SALE",
    badgeColor: "destructive",
    showCountdown: false,
  },
]

/**
 * Get active promotions for a given date
 */
export function getActivePromotions(date: Date = new Date()): Promotion[] {
  return PROMOTIONS.filter((promo) => {
    if (!promo.active) return false
    
    const start = new Date(promo.startDate)
    const end = new Date(promo.endDate)
    
    return date >= start && date <= end
  })
}

/**
 * Get the best active promotion for a plan and interval
 * Returns the promotion with the highest discount
 */
export function getBestPromotion(
  planName: "creator" | "dreamer",
  interval: "monthly" | "yearly",
  date: Date = new Date()
): Promotion | null {
  const activePromos = getActivePromotions(date)
  
  const applicable = activePromos.filter(
    (promo) =>
      promo.applicablePlans.includes(planName) &&
      promo.applicableIntervals.includes(interval)
  )
  
  if (applicable.length === 0) return null
  
  // Return the promotion with the highest discount
  return applicable.reduce((best, current) =>
    current.discountPercentage > best.discountPercentage ? current : best
  )
}

/**
 * Calculate discounted price
 */
export function calculateDiscountedPrice(
  originalPriceInCents: number,
  discountPercentage: number
): number {
  const discount = (originalPriceInCents * discountPercentage) / 100
  return Math.round(originalPriceInCents - discount)
}

/**
 * Format discounted price for display
 */
export function formatDiscountedPrice(
  originalPriceInCents: number,
  discountPercentage: number,
  interval: "monthly" | "yearly"
): { original: string; discounted: string; savings: string } {
  const discounted = calculateDiscountedPrice(originalPriceInCents, discountPercentage)
  const savings = originalPriceInCents - discounted
  
  const formatPrice = (cents: number) => {
    if (interval === "yearly") {
      return `$${cents / 100}/year`
    }
    return `$${cents / 100}`
  }
  
  return {
    original: formatPrice(originalPriceInCents),
    discounted: formatPrice(discounted),
    savings: `Save $${(savings / 100).toFixed(2)}`,
  }
}

/**
 * Get time remaining until promotion ends
 */
export function getTimeRemaining(endDate: string): {
  days: number
  hours: number
  minutes: number
  seconds: number
  isExpired: boolean
} {
  const now = new Date()
  const end = new Date(endDate)
  const diff = end.getTime() - now.getTime()
  
  if (diff <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, isExpired: true }
  }
  
  const days = Math.floor(diff / (1000 * 60 * 60 * 24))
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
  const seconds = Math.floor((diff % (1000 * 60)) / 1000)
  
  return { days, hours, minutes, seconds, isExpired: false }
}

/**
 * Check if user qualifies for early client offer
 * This would typically check against a database of early signups
 */
export async function qualifiesForEarlyClient(userId?: string): Promise<boolean> {
  // TODO: Implement logic to check if user signed up before a certain date
  // or is within the first N users
  // For now, return true if the promotion is active
  const earlyClientPromo = PROMOTIONS.find((p) => p.offerType === "early_client")
  if (!earlyClientPromo || !earlyClientPromo.active) return false
  
  const now = new Date()
  const start = new Date(earlyClientPromo.startDate)
  const end = new Date(earlyClientPromo.endDate)
  
  return now >= start && now <= end
}
